import React, { createContext, useState, useEffect, useCallback } from 'react';
import { AuthResponse, UsuarioInfo } from '../tipos/vacaciones';
import { getStoredToken, getStoredRefreshToken, setStoredTokens, clearStoredTokens } from '../services/api';
import * as authService from '../services/authService';

interface AuthContextType {
  usuario: UsuarioInfo | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
}

export const AuthContext = createContext<AuthContextType>({
  usuario: null,
  token: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  isAdmin: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<UsuarioInfo | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState(true);

  const cargarUsuario = useCallback(async () => {
    const storedToken = getStoredToken();
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const info = await authService.getMe(storedToken);
      setUsuario(info);
      setToken(storedToken);
    } catch {
      // Token invalido o expirado - intentar refresh
      const storedRefresh = getStoredRefreshToken();
      if (storedRefresh) {
        try {
          const response = await authService.refreshToken(storedToken, storedRefresh);
          setStoredTokens(response.token, response.refreshToken);
          setUsuario(response.usuario);
          setToken(response.token);
        } catch {
          clearStoredTokens();
          setUsuario(null);
          setToken(null);
        }
      } else {
        clearStoredTokens();
        setUsuario(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarUsuario();
  }, [cargarUsuario]);

  const login = async (email: string, password: string) => {
    const response: AuthResponse = await authService.login({ email, password });
    setStoredTokens(response.token, response.refreshToken);
    setUsuario(response.usuario);
    setToken(response.token);
  };

  const logout = async () => {
    const storedToken = getStoredToken();
    const storedRefresh = getStoredRefreshToken();
    if (storedRefresh && storedToken) {
      try {
        await authService.revokeToken(storedRefresh, storedToken);
      } catch {
        // Ignorar errores al revocar - igual cerramos sesion
      }
    }
    clearStoredTokens();
    setUsuario(null);
    setToken(null);
  };

  const isAdmin = () => usuario?.rol === 'Admin';

  return (
    <AuthContext.Provider value={{ usuario, token, loading, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};
